/**
 * Utilities for subscription status checking and normalization
 * Following Single Responsibility Principle (SOLID)
 */

/**
 * Normalize subscription status to uppercase
 * @param {string} status - Raw status string
 * @returns {string} Normalized uppercase status
 */
export function normalizeStatus(status) {
  return (status || '').toUpperCase();
}

/**
 * Check if subscription is in a processing state
 * @param {Object|string} subscription - Subscription object or status string
 * @returns {boolean} True if in PROCESSING or INITIATED state
 */
export function isProcessingStatus(subscription) {
  const status = typeof subscription === 'string' 
    ? subscription 
    : subscription?.status;
  
  if (!status) return false;
  
  const normalized = normalizeStatus(status);
  return normalized === 'PROCESSING' || normalized === 'INITIATED';
}

/**
 * Check if subscription is active (including grace period)
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if subscription is active
 */
export function isActiveSubscription(subscription) {
  if (!subscription) return false;
  
  const normalized = normalizeStatus(subscription.status);
  
  // Check for direct active status
  if (normalized === 'ACTIVE' || normalized === 'SUCCESS') {
    return true;
  }
  
  // Check for cancelled but in grace period
  if ((normalized === 'CANCELLED' || normalized === 'CANCELED') &&
      subscription.gracePeriod !== null && 
      subscription.gracePeriod >= 0) {
    return true;
  }
  
  return false;
}

/**
 * Check if subscription is blocked from changes
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if blocked
 */
export function isBlockedStatus(subscription) {
  if (!subscription) return false;
  
  const normalized = normalizeStatus(subscription.status);
  return ['PROCESSING', 'INITIATED', 'RETRYING', 'SUSPENDED'].includes(normalized);
}

/**
 * Find active subscription for a specific team
 * @param {Array} subscriptions - Array of subscriptions
 * @param {string} teamId - Team ID to find
 * @returns {Object|null} Active subscription or null
 */
export function findActiveSubscription(subscriptions, teamId) {
  if (!subscriptions || !Array.isArray(subscriptions)) {
    return null;
  }
  
  const items = Array.isArray(subscriptions) ? subscriptions : [subscriptions];
  
  return items.find(s => {
    if (teamId && s.teamId !== teamId) {
      return false;
    }
    return isActiveSubscription(s);
  }) || null;
}

/**
 * Find processing subscription from array
 * @param {Array} subscriptions - Array of subscriptions
 * @returns {Object|null} Processing subscription or null
 */
export function findProcessingSubscription(subscriptions) {
  if (!subscriptions || !Array.isArray(subscriptions)) {
    return null;
  }
  
  return subscriptions.find(s => isProcessingStatus(s)) || null;
}
